import { gql } from '@apollo/client';

/**
 * White-label CUSTOM-DOMAIN social login — the bounce flow (scio3#2214).
 *
 * `signInWithPopup` cannot work on a customer's vanity domain: that origin would have to be
 * registered in Firebase's authorized-domain list AND in every provider's OAuth app, per customer.
 * So on a custom domain scio3 does what scio1 does — the provider redirects to ONE fixed host, and
 * the customer's domain travels as server-signed opaque `state`:
 *
 *   1. `getSocialLoginUrl(provider)`      → provider authorize URL (fixed `redirect_uri` + `state`)
 *   2. provider → `https://<fixed-host>/social/<provider>?code&state`          (hop 1)
 *   3. `getSocialLoginRedirect(state)`    → the VALIDATED destination origin, then bounce there
 *   4. `exchangeSocialLoginCode(...)`     → server-side code exchange + user provisioning (hop 2)
 *   5. popup postMessages the result to its opener → `signInWithCustomToken`
 *
 * **Backend contract — READ BEFORE CHANGING.** These three operations are the frontend half of
 * spec `claude-dev/ted/2026-09-24-social-login-custom-domain/01-spec.md` (T2/T3). The backend half
 * lands in `optisigns-open-api` and owns:
 * - the OAuth client id / secret / fixed redirect host — ALL config-driven (`ConfigService`), never
 *   literals, because spec decisions D1/D2 are still open. Nothing here carries a client id: the
 *   browser only ever receives a server-built authorize URL.
 * - `state` as a server-issued, signed, SINGLE-USE nonce. The client treats `state` as opaque and
 *   must never read a redirect target out of it — that is scio1's open redirect (spec §3 security).
 * - the destination allowlist: `getSocialLoginRedirect` re-validates the nonce's host against
 *   `domains.host` + `company.feature.brandingCustomDomain` before the client bounces anywhere.
 *
 * All three are `@Public()` — they run pre-login.
 */

/** Provider keys accepted by the backend. Derived from a Firebase `provider.providerId`. */
export type SocialLoginProvider = 'google' | 'facebook' | 'microsoft';

/** Map a Firebase `providerId` (`google.com`) to the backend provider key (`google`). */
export function toSocialLoginProvider(providerId: string): SocialLoginProvider | null {
  switch (providerId) {
    case 'google.com':
      return 'google';
    case 'facebook.com':
      return 'facebook';
    case 'microsoft.com':
      return 'microsoft';
    default:
      return null;
  }
}

/**
 * Map the provider slug to the GraphQL ENUM KEY the schema expects (`google` -> `GOOGLE`).
 *
 * `registerEnumType` registers TS enum KEYS as GraphQL values (repo-wide convention), so sending
 * the lowercase slug is rejected at validation with "Variable $provider of type String! used in
 * position expecting type SocialLoginProvider!".
 */
export type SocialLoginProviderEnumValue = Uppercase<SocialLoginProvider>;

export function toSocialLoginEnumValue(
  provider: SocialLoginProvider,
): SocialLoginProviderEnumValue {
  return provider.toUpperCase() as SocialLoginProviderEnumValue;
}

/**
 * Result type for GET_SOCIAL_LOGIN_URL.
 *
 * The backend returns an OBJECT (`SocialLoginUrlResult!`), not a bare string: `state` is echoed
 * so the opener can correlate the popup's postMessage with the request it started.
 */
export interface GetSocialLoginUrlResult {
  getSocialLoginUrl: { url: string; state: string };
}

/**
 * Variables type for GET_SOCIAL_LOGIN_URL.
 *
 * `host` is a HINT only. The authoritative host must be taken from the request server-side
 * (`ctx.req.headers.host`) — a client-supplied host would make the entitlement check decorative.
 * It is sent so the operation still works if the backend declares the argument required.
 */
export interface GetSocialLoginUrlVariables {
  /**
   * The GraphQL enum KEY (`GOOGLE`), not the lowercase slug — use `toSocialLoginEnumValue()`.
   *
   * Typed as the UPPERCASE union, not `string`: while it was `string`, passing the lowercase route
   * slug type-checked and then failed at runtime with BAD_USER_INPUT before the resolver ran.
   *
   * There is deliberately NO `host`: the server reads it from the request. A caller-supplied host
   * would make the entitled-custom-domain check decorative.
   */
  provider: SocialLoginProviderEnumValue;
}

/**
 * Authorize-URL mint for the calling custom domain. `@Public()`.
 *
 * Returns null (or errors) when the calling host is not a real, entitled custom domain — the
 * frontend then falls back to nothing and shows an error; it must NOT construct a URL itself.
 */
export const GET_SOCIAL_LOGIN_URL = gql`
  query GetSocialLoginUrl($provider: SocialLoginProvider!) {
    getSocialLoginUrl(provider: $provider) {
      url
      state
    }
  }
`;

/** Result type for GET_SOCIAL_LOGIN_REDIRECT — validated destination ORIGIN (`https://host`). */
export interface GetSocialLoginRedirectResult {
  getSocialLoginRedirect: string | null;
}

/** Variables type for GET_SOCIAL_LOGIN_REDIRECT. `state` is opaque — pass it through verbatim. */
export interface GetSocialLoginRedirectVariables {
  state: string;
}

/**
 * Hop 1: resolve where a callback may be bounced. `@Public()`.
 *
 * Verifies the `state` signature and returns the ORIGIN it was issued for, re-checked against the
 * `domains` collection + the `brandingCustomDomain` add-on. Null ⇒ forged / expired / not entitled,
 * and the callback page must refuse rather than guess.
 *
 * This is deliberately NOT a client-side parse of `state`: reading the host out of an unverified
 * blob and redirecting there is exactly the open redirect / code-exfiltration hole in scio1
 * (`socialGoogle.js:40`). It also does NOT consume the nonce — `exchangeSocialLoginCode` does.
 */
export const GET_SOCIAL_LOGIN_REDIRECT = gql`
  query GetSocialLoginRedirect($state: String!) {
    getSocialLoginRedirect(state: $state)
  }
`;

/**
 * Payload of EXCHANGE_SOCIAL_LOGIN_CODE.
 *
 * `tokenId` is an id for a Firebase custom token cached server-side (60s TTL), NOT the token —
 * same handoff SAML and team invites already use (`GET_CUSTOM_LOGIN_TOKEN`), which keeps the raw
 * custom token out of the GraphQL response body and makes it single-use.
 *
 * `isNewUser` / `name` / `email` are load-bearing, not conveniences: after
 * `signInWithCustomToken` Firebase populates NO `AdditionalUserInfo`, so
 * `getAdditionalUserInfo(credential)?.isNewUser` is always false on this path and the signup
 * analytics + Get-Started checklist would silently never fire. The server tells us instead.
 */
export interface SocialLoginExchange {
  /**
   * The Firebase custom token ITSELF, not an id to trade.
   *
   * An earlier draft expected a `tokenId` redeemed through the SAML-style `getCustomLoginToken`.
   * The backend returns the token directly (spec D4), and the handoff to the opener is
   * same-origin — after hop 1 the popup sits on the customer's domain, the same origin as the
   * page that opened it — so the token never crosses an origin boundary.
   */
  customToken: string;
  isNewUser: boolean;
  displayName?: string | null;
  email?: string | null;
}

/** Result type for EXCHANGE_SOCIAL_LOGIN_CODE */
export interface ExchangeSocialLoginCodeResult {
  exchangeSocialLoginCode: SocialLoginExchange | null;
}

/** Variables type for EXCHANGE_SOCIAL_LOGIN_CODE — `code` and `state` are passed through opaquely. */
export interface ExchangeSocialLoginCodeVariables {
  /** GraphQL enum KEY — use `toSocialLoginEnumValue()`. Never the lowercase slug. */
  provider: SocialLoginProviderEnumValue;
  code: string;
  state: string;
  /**
   * Signup attribution, read from the SAME-ORIGIN cookies the opener sees (the popup shares its
   * origin after hop 1). Omitted for a returning user — the server ignores it when the identity
   * already exists.
   *
   * This exists because the popup path's follow-up `register` call cannot work here: the bounce
   * signs in with a custom token, so `sign_in_provider` is `custom` and `register` lands in the
   * password branch. Exactly the two fields the social branch consumes; `refCode` is NOT one of
   * them on any path today.
   */
  signup?: SocialLoginSignupPayload;
}

/** Attribution carried on the exchange — mirrors the open-api `SocialLoginSignupInput`. */
export interface SocialLoginSignupPayload {
  visitorId?: string;
  utmData?: Record<string, string | undefined>;
}

/**
 * Hop 2: exchange the authorization code for a Firebase custom token. `@Public()`.
 *
 * Server-side only — `client_secret` never reaches the browser. Consumes the `state` nonce (replay
 * ⇒ rejected) and resolves-or-creates the user through the same idempotent engine `register` uses,
 * which is what makes "register by social" work on a custom domain.
 */
export const EXCHANGE_SOCIAL_LOGIN_CODE = gql`
  mutation ExchangeSocialLoginCode(
    $provider: SocialLoginProvider!
    $code: String!
    $state: String!
    $signup: SocialLoginSignupInput
  ) {
    exchangeSocialLoginCode(provider: $provider, code: $code, state: $state, signup: $signup) {
      customToken
      isNewUser
      displayName
      email
    }
  }
`;
